import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, createVNode as _createVNode, resolveDynamicComponent as _resolveDynamicComponent, withCtx as _withCtx } from "vue"

import { computed, reactive, ref } from 'vue';
import { formatMinuteTimeNoTZ } from '../filters';
import { ProcedureDocumentType } from '@/lib/file';
import api from '../api';
import config from '../config';
import procedureStep from "../components/ProcedureStep"
import ProcedurePage from "../components/ProcedurePage.vue";
import FileInput from "@intec/vue-components/form/FileInput.vue";
import SubjectInfo from "../components/procedure/SubjectInfo.vue";
import ContactPerson from '../components/procedure/ContactPerson.vue';
import AgreementInfo from "../components/procedure/AgreementInfo.vue";
import PositionsView from "../components/positions/PositionsView.vue"
import SpecificationOrder from '../components/procedure/SpecificationOrder.vue';
import WinnersInfo from '../components/procedure/WinnersInfo.vue';
import Config from "@/config";
import { useLibraryStore } from "@/stores/library";
import { useProcedureViewStore } from "@/stores/procedureViewStore";
import { useViewProcedureStageComponentStore } from "@/stores/viewProcedureStageComponentStore";
import OpenCloseInfo from "@/components/procedure/OpenCloseInfo.vue";
import CategoriesView from '@/components/categories/CategoriesView.vue';
import ProcedureStageInfo from '@/components/procedure/ProcedureStageInfo.vue';
import FileIcon from "@/components/FileIcon.vue";
import CancellationReasonsView from '@/components/procedure/CancellationReasonsView.vue';

interface Procedure {
  id?: number
  registryNumber?: string
  type?: string
  goodName?: string
  subject?: string
  goodQuantityUnits?: string
  startMaxPrice?: string
  applicationDeadline?: null
  reviewDeadline?: null
  summingStart?: null
  shippingCondition?: string
  statusText?: string
  publishedAt?: null
  organizerName?: string
  status: {
    name: string
    code: string
  }
  orgFullName?: string,
  isWithoutStartMaxPrice?: boolean,
  isNds?: boolean,
  partOfVolume: boolean
  withPositions: boolean,
  additionalInformation?: string,
  edoUrl?: string
  mode: string
  [key: string]: any
}

interface SupplierRequirement {
  type: string
  name: string
  isRequired: boolean
  description: string
  list: Array<{
    value: string
  }>
}

interface Categories {
  id: number,
  name: string
}

interface Position {
  id?: number,
  name: string,
  measure: string,
  quantity: number,
  description?: string
}

interface Access {
  canViewCompletedProcedure: boolean;
  canApplyApplication: boolean;
  canCancel: boolean;
  canEdit: boolean;
  canEditApplication: boolean;
  canRecall: boolean;
  canReview: boolean;
  canSumming: boolean;
  canView: boolean;
  canCopy: boolean;
  canViewWinners: boolean;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'ProcedureView',
  setup(__props) {

const procedure = reactive<Procedure>({
  id: null,
  registryNumber: '',
  type: '',
  goodName: '',
  subject: '',
  goodQuantityUnits: '',
  startMaxPrice: '',
  applicationDeadline: null,
  reviewDeadline: null,
  summingStart: null,
  shippingCondition: '',
  statusText: '',
  publishedAt: null,
  organizerName: '',
  status: { name: '', code: '' },
  orgFullName: '',
  isWithoutStartMaxPrice: false,
  isNds: null,
  partOfVolume: false,
  withPositions: false,
  additionalInformation: null,
  contactPerson: {
    userFullName: null,
    userPhone: null,
    userEmail: null
  },
  isClosed: false,
  allowedContragents: [],
  mode: 'ENTIRE'
});
const types = ref<{
  title: string
  value: string
}[]>([]);
const procedureActions = reactive({
  primaryAction: [],
  secondaryActions: [],
});
const supplierRequirements = reactive<Array<SupplierRequirement>>([]);
const categories = reactive<Array<Categories>>([]);
const positions = reactive<Array<Position>>([]);
const access = reactive<Access>({
  canViewCompletedProcedure: false,
  canApplyApplication: false,
  canCancel: false,
  canEdit: false,
  canEditApplication: false,
  canRecall: false,
  canReview: false,
  canSumming: false,
  canView: false,
  canCopy: false,
  canViewWinners: false,
});
const loadingDialogText = ref<string>();
const procedureDocuments = ref<object[]>([]);
const procedureDocumentsProject = ref<object[]>([]);
const agreementDocuments = ref<object[]>([]);
const winners = reactive([]);
const result = reactive({
  isCurrentContragentWinner: false,
  cancellationReasons: [],
});
const isAdmin = config.getUser().hasRole('ROLE_ADMIN');
const isOrg = config.getUser().hasRole('ROLE_CUSTOMER');
const isHiddenContractPerson = config.getConfig('HIDDEN_CONTACT_PERSON');
const showStartMaxPrice = ref<boolean>(false);
const showTitleHint = ref<boolean>(true);
const showPriceQuantity = ref<boolean>(false);
const tableReplaceCaption = ref<string>('');

const actionsInfo = computed<object>(
  () => ({
    p: {
      id: procedure.id,
      registryNumber: procedure.registryNumber
    },
    access,
    procedureActions,
  })
);

const canViewOpenCloseInfo = computed(() => {
  const user = Config.getUser();
  return user.hasRole('ROLE_ADMIN') || user.hasRole('ROLE_CUSTOMER');
});

const viewStageComponent = computed(() => {
  const viewStageStore = useViewProcedureStageComponentStore();

  return viewStageStore.getComponentByType(procedure.type) || ProcedureStageInfo;
});

const isGuest = computed(() => {
  return Config.isGuest();
});

const libraryStore = useLibraryStore();
const procedureViewStore = useProcedureViewStore();

const load = async (procedureId, isPublicRoute) => {
  const url = isPublicRoute ? '/open/procedures/' + procedureId : '/procedures/view/' + procedureId;

  const resp = await api.get(url);
  Object.assign(winners, resp.winners);
  Object.assign(result, resp.result);
  Object.assign(categories, resp.categories);
  Object.assign(positions, resp.positions);
  Object.assign(procedure, resp.procedure);
  Object.assign(procedureActions, resp.procedureActions);
  Object.assign(access, resp.access);
  Object.assign(supplierRequirements, resp.supplierRequirements)
  agreementDocuments.value = resp.agreementDocuments;
  procedureDocuments.value = resp.procedureDocuments;
  procedureDocumentsProject.value = resp.procedureDocumentsProject;
  types.value = resp.types.map((elem) => ({
    title: elem.name,
    value: elem.code,
  }));

  libraryStore.callActionByCode('initProcedureViewOptions', {
    procedureType: procedure.type,
  });
  const data = libraryStore.callFunctionByCode('getPositionsViewOptions', {
    procedureType: procedure.type,
    procedureStep: procedure.status.code,
  });
  showStartMaxPrice.value = data.showStartMaxPrice ?? false;
  showTitleHint.value = data.showTitleHint ?? true;
  showPriceQuantity.value = data.showPriceQuantity ?? false;
};

const onLoadError = error => {
  if (error.name !== 403) {
    console.error(666, error.message);
  }
};

const downloadDocument = async (documentId: number) => {
  window.location.href = '/procedures/downloadDocument/' + documentId;
};

const downloadAgreementDocument = async (documentId: number) => {
  window.location.href = '/procedures/downloadAgreementDocument/' + documentId;
};

const downloadDocuments = async (procedureId: number, documentType: number) => {
  window.location.href = `/procedures/downloadDocuments/${procedureId}/${documentType}`;
};

const downloadAgreementDocuments = async (procedureId: number) => {
  window.location.href = '/procedures/downloadAgreementDocuments/' + procedureId;
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(ProcedurePage, {
    id: _ctx.$route.params.id,
    procedure: procedure,
    result: result,
    "actions-info": actionsInfo.value,
    "load-func": load,
    "loading-dialog-text": loadingDialogText.value,
    status: procedure.status,
    types: types.value,
    isClosed: procedure.isClosed,
    "show-cancel-details": true,
    "onUpdate:procedureGoodName": _cache[8] || (_cache[8] = ($event: any) => (procedure.goodName = $event)),
    "onUpdate:procedureType": _cache[9] || (_cache[9] = ($event: any) => (procedure.type = $event)),
    onLoadError: onLoadError
  }, {
    default: _withCtx(() => [
      (procedure.cancellationReasons?.length > 0)
        ? (_openBlock(), _createBlock(CancellationReasonsView, {
            key: 0,
            cancellationReasons: procedure.cancellationReasons
          }, null, 8 /* PROPS */, ["cancellationReasons"]))
        : _createCommentVNode("v-if", true),
      (!_unref(isHiddenContractPerson) && procedure.orgFullName)
        ? (_openBlock(), _createBlock(ContactPerson, {
            key: 1,
            organization: procedure.orgFullName,
            contact: procedure.contactPerson,
            view: true
          }, null, 8 /* PROPS */, ["organization", "contact"]))
        : _createCommentVNode("v-if", true),
      (
        procedure.status.code === _unref(procedureStep).COMPLETED &&
        (procedure.mode === 'PARTIAL' && !_unref(procedureViewStore).needOfferPrice) &&
        result.isCurrentContragentWinner
      )
        ? (_openBlock(), _createBlock(SpecificationOrder, {
            key: 2,
            result: result,
            procedure: procedure
          }, null, 8 /* PROPS */, ["result", "procedure"]))
        : _createCommentVNode("v-if", true),
      (access.canViewWinners)
        ? (_openBlock(), _createBlock(WinnersInfo, {
            key: 3,
            winners: winners,
            withPositions: procedure.withPositions,
            "procedure-step-code": procedure.status.code,
            procedure: procedure
          }, null, 8 /* PROPS */, ["winners", "withPositions", "procedure-step-code", "procedure"]))
        : _createCommentVNode("v-if", true),
      _createVNode(SubjectInfo, {
        subject: procedure.subject,
        "onUpdate:subject": _cache[0] || (_cache[0] = ($event: any) => ((procedure.subject) = $event)),
        "start-max-price": procedure.startMaxPrice,
        "onUpdate:startMaxPrice": _cache[1] || (_cache[1] = ($event: any) => ((procedure.startMaxPrice) = $event)),
        "is-without-start-max-price": procedure.isWithoutStartMaxPrice,
        "onUpdate:isWithoutStartMaxPrice": _cache[2] || (_cache[2] = ($event: any) => ((procedure.isWithoutStartMaxPrice) = $event)),
        "is-nds": procedure.isNds,
        "onUpdate:isNds": _cache[3] || (_cache[3] = ($event: any) => ((procedure.isNds) = $event)),
        "additional-information": procedure.additionalInformation,
        "onUpdate:additionalInformation": _cache[4] || (_cache[4] = ($event: any) => ((procedure.additionalInformation) = $event)),
        "procedure-step-code": procedure.status.code,
        "procedure-type": procedure.type,
        procedure: procedure,
        result: result
      }, null, 8 /* PROPS */, ["subject", "start-max-price", "is-without-start-max-price", "is-nds", "additional-information", "procedure-step-code", "procedure-type", "procedure", "result"]),
      (viewStageComponent.value)
        ? (_openBlock(), _createBlock(_resolveDynamicComponent(viewStageComponent.value), {
            key: 4,
            procedure: procedure,
            "supplier-requirements": supplierRequirements
          }, null, 8 /* PROPS */, ["procedure", "supplier-requirements"]))
        : _createCommentVNode("v-if", true),
      (categories.length > 0)
        ? (_openBlock(), _createBlock(CategoriesView, {
            key: 5,
            "card-title": _ctx.$t('Категории поставщиков'),
            categories: categories
          }, null, 8 /* PROPS */, ["card-title", "categories"]))
        : _createCommentVNode("v-if", true),
      (positions?.length)
        ? (_openBlock(), _createBlock(PositionsView, {
            key: 6,
            positions: positions,
            partOfVolume: procedure.partOfVolume,
            "procedure-type": procedure.type,
            "is-nds": procedure.isNds,
            "show-start-max-price": showStartMaxPrice.value,
            "show-title-hint": showTitleHint.value,
            "show-price-quantity": showPriceQuantity.value,
            "table-replace-caption": tableReplaceCaption.value
          }, null, 8 /* PROPS */, ["positions", "partOfVolume", "procedure-type", "is-nds", "show-start-max-price", "show-title-hint", "show-price-quantity", "table-replace-caption"]))
        : _createCommentVNode("v-if", true),
      _createVNode(FileInput, {
        documents: procedureDocuments.value,
        "download-file": downloadDocument,
        "download-all-files": () => downloadDocuments(_ctx.$route.params.id, _unref(ProcedureDocumentType).PROCEDURE.code)
      }, {
        "file-icon": _withCtx(({ document }) => [
          _createVNode(FileIcon, { document: document }, null, 8 /* PROPS */, ["document"])
        ]),
        _: 1 /* STABLE */
      }, 8 /* PROPS */, ["documents", "download-all-files"]),
      (!isGuest.value)
        ? (_openBlock(), _createBlock(FileInput, {
            key: 7,
            title: _ctx.$t('Проект договора'),
            documents: procedureDocumentsProject.value,
            "download-file": downloadDocument,
            "download-all-files": () => downloadDocuments(_ctx.$route.params.id, _unref(ProcedureDocumentType).PROJECT_AGREEMENT.code)
          }, {
            "file-icon": _withCtx(({ document }) => [
              _createVNode(FileIcon, { document: document }, null, 8 /* PROPS */, ["document"])
            ]),
            _: 1 /* STABLE */
          }, 8 /* PROPS */, ["title", "documents", "download-all-files"]))
        : _createCommentVNode("v-if", true),
      (agreementDocuments.value.length && procedure.status.code != _unref(procedureStep).AGREEMENT
        && (_unref(isAdmin) || _unref(isOrg)))
        ? (_openBlock(), _createBlock(FileInput, {
            key: 8,
            title: _ctx.$t('Документы договора'),
            documents: agreementDocuments.value,
            "download-file": downloadAgreementDocument,
            "download-all-files": () => downloadAgreementDocuments(_ctx.$route.params.id)
          }, {
            "file-icon": _withCtx(({ document }) => [
              _createVNode(FileIcon, { document: document }, null, 8 /* PROPS */, ["document"])
            ]),
            _: 1 /* STABLE */
          }, 8 /* PROPS */, ["title", "documents", "download-all-files"]))
        : _createCommentVNode("v-if", true),
      (procedure.edoUrl && procedure.status.code != _unref(procedureStep).AGREEMENT
        && (_unref(isAdmin) || _unref(isOrg)))
        ? (_openBlock(), _createBlock(AgreementInfo, {
            key: 9,
            agreement: procedure.edoUrl,
            "onUpdate:agreement": _cache[5] || (_cache[5] = ($event: any) => ((procedure.edoUrl) = $event))
          }, null, 8 /* PROPS */, ["agreement"]))
        : _createCommentVNode("v-if", true),
      (canViewOpenCloseInfo.value && procedure.allowedContragents.length)
        ? (_openBlock(), _createBlock(OpenCloseInfo, {
            key: 10,
            view: "",
            "is-closed": procedure.isClosed,
            "onUpdate:isClosed": _cache[6] || (_cache[6] = ($event: any) => ((procedure.isClosed) = $event)),
            allowedContragents: procedure.allowedContragents,
            "onUpdate:allowedContragents": _cache[7] || (_cache[7] = ($event: any) => ((procedure.allowedContragents) = $event))
          }, null, 8 /* PROPS */, ["is-closed", "allowedContragents"]))
        : _createCommentVNode("v-if", true)
    ]),
    _: 1 /* STABLE */
  }, 8 /* PROPS */, ["id", "procedure", "result", "actions-info", "loading-dialog-text", "status", "types", "isClosed"]))
}
}

})